import { useEffect, useRef, useState } from 'react'
import { supabase } from './supabase'

/**
 * Hear rows change (Speed and Feel L2). `onChange` runs on every insert, update or delete that matches; the hook returns
 * whether the live link is up. The screens keep their poll either way: at its old pace while the link is down or not yet
 * confirmed, and slowed to a fallback once the link is up, so a screen flips the moment Shopify confirms and still flips
 * within seconds if Realtime is ever off.
 */
export function useLiveRows(table: string, filter: string | null, onChange: () => void): boolean {
  const [up, setUp] = useState(false)
  const cb = useRef(onChange); cb.current = onChange
  useEffect(() => {
    if (filter === null) return
    setUp(false)
    // A unique topic per hook instance. supabase.channel() hands back the existing channel for a topic already open, and
    // adding a listener to a channel that is already subscribed throws, which took the whole app down (29 Sep: the rail's
    // Inbox count and the Inbox screen both listened to web_submissions).
    const ch = supabase.channel(`live:${table}:${filter}:${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) }, () => cb.current())
      .subscribe((status) => setUp(status === 'SUBSCRIBED'))
    return () => { setUp(false); void supabase.removeChannel(ch) }
  }, [table, filter])
  return up
}

/** The poll interval for a screen: its usual pace, or the slow fallback while the live link carries the changes. */
export const pollEvery = (live: boolean, usual = 3000, fallback = 15000) => (live ? fallback : usual)
